import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { Plus, Trash2 } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  text_fa: "",
  text_en: "",
  blanks: [{ key: "1", answers: [{ fa: "", en: "" }] }],
  bank: [
    { key: "w1", fa: "", en: "" },
    { key: "w2", fa: "", en: "" },
  ],
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";

  const body = item.body || {};
  const text_fa = body?.text?.fa || "";
  const text_en = body?.text?.en || "";
  const layout = body.layout || "top-bottom";

  const blanksRaw = Array.isArray(body.blanks) ? body.blanks : [];
  const blanks =
    blanksRaw.length > 0
      ? blanksRaw.map((b, idx) => {
          const key = String(b.key || idx + 1);
          const answersRaw = Array.isArray(b.answers) ? b.answers : [];
          const answers =
            answersRaw.length > 0
              ? answersRaw.map((a) => ({
                  fa: a?.fa || "",
                  en: a?.en || "",
                }))
              : [{ fa: "", en: "" }];

          return { key, answers };
        })
      : [{ key: "1", answers: [{ fa: "", en: "" }] }];

  const bankRaw = Array.isArray(body.bank) ? body.bank : [];
  const bank =
    bankRaw.length > 0
      ? bankRaw.map((w, idx) => ({
          key: w.key || `w${idx + 1}`,
          fa: w?.label?.fa || "",
          en: w?.label?.en || "",
        }))
      : [
          { key: "w1", fa: "", en: "" },
          { key: "w2", fa: "", en: "" },
        ];

  const points = item.points ?? 1;
  const status = item.status || "published";
  const tags = Array.isArray(item.tags) ? item.tags.join(",") : "";
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    text_fa,
    text_en,
    blanks,
    bank,
    layout,
    points,
    status,
    tags,
    explanation_fa,
    explanation_en,
  };
};

export default function WordBankModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const addBlank = () =>
    setForm((f) => ({
      ...f,
      blanks: [
        ...f.blanks,
        { key: String(f.blanks.length + 1), answers: [{ fa: "", en: "" }] },
      ],
    }));
  const removeBlank = (i) =>
    setForm((f) => ({ ...f, blanks: f.blanks.filter((_, idx) => idx !== i) }));
  const addAnswer = (bi) =>
    setForm((f) => {
      const blanks = [...f.blanks];
      blanks[bi].answers.push({ fa: "", en: "" });
      return { ...f, blanks };
    });
  const removeAnswer = (bi, ai) =>
    setForm((f) => {
      const blanks = [...f.blanks];
      blanks[bi].answers = blanks[bi].answers.filter((_, i) => i !== ai);
      return { ...f, blanks };
    });

  const addBankWord = () =>
    setForm((f) => ({
      ...f,
      bank: [...f.bank, { key: `w${f.bank.length + 1}`, fa: "", en: "" }],
    }));
  const removeBankWord = (i) =>
    setForm((f) => ({ ...f, bank: f.bank.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text_fa.trim() && !form.text_en.trim()) {
      toast.error(t("word_bank.toasts.text_required"));
      return;
    }

    const payload = {
      kind: "word_bank",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        text: {
          fa: form.text_fa || undefined,
          en: form.text_en || undefined,
        },
        blanks: form.blanks.map((b) => ({
          key: b.key,
          answers: b.answers
            .map((a) => ({
              fa: a.fa || undefined,
              en: a.en || undefined,
            }))
            .filter((a) => a.fa || a.en),
        })),
        bank: form.bank
          .map((w) => ({
            key: w.key,
            label: {
              fa: w.fa || undefined,
              en: w.en || undefined,
            },
          }))
          .filter((w) => w.label.fa || w.label.en),
        layout: form.layout,
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("word_bank.toasts.updated", "سؤال Word Bank با موفقیت ویرایش شد")
          : t("word_bank.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("word_bank.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("word_bank.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("word_bank.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("word_bank.stem_en_ph")}
            directionality="ltr"
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />
        </div>
      </div>

      {/* Layout Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("word_bank.layout_label")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`word_bank.layout_question_answer_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>

      {/* Passage with {{n}} markers */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("word_bank.text")}
        </label>
        <div dir={isRtl ? "rtl" : "ltr"}>
          <CustomEditor
            value={form.text_fa}
            onEditorChange={(content) => handleEditorChange("text_fa", content)}
            placeholder={t("word_bank.text_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
            height={200}
            toolbar="undo redo | bold italic underline"
          />
        </div>
        <div dir="ltr" className="mt-2">
          <CustomEditor
            value={form.text_en}
            onEditorChange={(content) => handleEditorChange("text_en", content)}
            placeholder={t("word_bank.text_en_ph")}
            directionality="ltr"
            height={200}
            toolbar="undo redo | bold italic underline"
          />
        </div>
      </div>

      {/* Blanks (answers per blank key) */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("word_bank.blanks")}
          </label>
          <button
            type="button"
            className="text-blue-600 text-sm"
            onClick={addBlank}
          >
            <Plus size={16} />
            {t("word_bank.add_blank")}
          </button>
        </div>
        <div className="space-y-3">
          {form.blanks.map((b, bi) => (
            <div key={bi} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs">{t("word_bank.blank_key")}</span>
                  <input
                    className="border rounded px-2 py-1 w-24"
                    value={b.key}
                    onChange={(e) => {
                      const blanks = [...form.blanks];
                      blanks[bi].key = e.target.value;
                      setForm((f) => ({ ...f, blanks }));
                    }}
                  />
                </div>
                {form.blanks.length > 1 && (
                  <button
                    type="button"
                    className="text-red-600 text-xs"
                    onClick={() => removeBlank(bi)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {b.answers.map((a, ai) => (
                  <div
                    key={ai}
                    className="grid grid-cols-1 md:grid-cols-2 gap-2"
                  >
                    <input
                      className="border rounded-lg px-3 py-2"
                      placeholder={t("word_bank.answer_fa")}
                      value={a.fa}
                      onChange={(e) => {
                        const blanks = [...form.blanks];
                        blanks[bi].answers[ai].fa = e.target.value;
                        setForm((f) => ({ ...f, blanks }));
                      }}
                    />
                    <div className="flex gap-2">
                      <input
                        className="border rounded-lg px-3 py-2 flex-1"
                        placeholder={t("word_bank.answer_en")}
                        value={a.en}
                        onChange={(e) => {
                          const blanks = [...form.blanks];
                          blanks[bi].answers[ai].en = e.target.value;
                          setForm((f) => ({ ...f, blanks }));
                        }}
                      />
                      {b.answers.length > 1 && (
                        <button
                          type="button"
                          className="px-2 rounded border text-red-600"
                          onClick={() => removeAnswer(bi, ai)}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="text-blue-600 text-xs inline-flex items-center gap-1"
                  onClick={() => addAnswer(bi)}
                >
                  <Plus size={16} />
                  {t("word_bank.add_answer")}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bank words */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium">
            {t("word_bank.bank")}
          </label>
          <button
            type="button"
            className="text-blue-600 text-sm"
            onClick={addBankWord}
          >
            <Plus size={16} />
            {t("word_bank.add_bank_word")}
          </button>
        </div>
        <div className="space-y-2">
          {form.bank.map((w, wi) => (
            <div key={wi} className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                className="border rounded-lg px-3 py-2"
                placeholder={t("word_bank.word_key_ph")}
                value={w.key}
                onChange={(e) => {
                  const bank = [...form.bank];
                  bank[wi].key = e.target.value;
                  setForm((f) => ({ ...f, bank }));
                }}
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder={t("word_bank.word_fa")}
                value={w.fa}
                onChange={(e) => {
                  const bank = [...form.bank];
                  bank[wi].fa = e.target.value;
                  setForm((f) => ({ ...f, bank }));
                }}
              />
              <div className="flex gap-2">
                <input
                  className="border rounded-lg px-3 py-2 flex-1"
                  placeholder={t("word_bank.word_en")}
                  value={w.en}
                  onChange={(e) => {
                    const bank = [...form.bank];
                    bank[wi].en = e.target.value;
                    setForm((f) => ({ ...f, bank }));
                  }}
                />
                {form.bank.length > 1 && (
                  <button
                    type="button"
                    className="px-2 rounded border text-red-600"
                    onClick={() => removeBankWord(wi)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("word_bank.points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={onChange}
            name="points"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("word_bank.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={onChange}
            name="status"
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("word_bank.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={onChange}
            name="tags"
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>
    </form>
  );
}
